import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Lightning, ShieldCheck, ChatCircle, FilePdf, Sparkle, Clock } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const HOOKS = [
  { i: ChatCircle, t: "Guided conversation", d: "Eleven short steps you walk through at your pace — not a 40-field form. Skip whatever you can't answer yet." },
  { i: Sparkle, t: "Your Idea Partner thinks with you", d: "An Appistry AI Idea Partner asks one focused follow-up where it matters — never to fill space." },
  { i: FilePdf, t: "You leave with a pack", d: "A branded PDF discovery summary in your inbox the moment you submit." },
  { i: Clock, t: "Save and come back", d: "Step away at any point. We email you a private link to pick up where you left off." },
];

const STEPS = [
  "A quick steer — your organisation & what you're exploring",
  "About you — your contact details",
  "About you & your business",
  "Your customers",
  "What you'd like to offer",
  "How people will access it",
  "AI & personalisation",
  "Existing business setup",
  "Branding & style",
  "Budget & next steps",
  "Wrap-up — final notes & consent",
];

export default function IdeaLab() {
  const navigate = useNavigate();

  const startSession = async () => {
    try {
      const { data } = await api.post("/idealab/drafts");
      // Persist token locally for autosave continuity
      try { localStorage.setItem("appistry.idealab.token", data.resume_token); } catch (_) {}
      navigate(`/idealab/start?resume=${data.resume_token}`);
    } catch (e) {
      navigate("/idealab/start");
    }
  };

  // If a token already exists in localStorage and we have no resume param, offer continue
  const localToken = (() => { try { return localStorage.getItem("appistry.idealab.token") || ""; } catch (_) { return ""; } })();

  return (
    <div data-testid="idealab-landing">
      {/* HERO */}
      <section className="relative overflow-hidden bg-zinc-950 text-white">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle, #FFFFFF 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
        <div className="absolute right-[-15%] top-[-25%] h-[700px] w-[700px] rounded-full opacity-30 blur-3xl pointer-events-none" style={{ background: "radial-gradient(circle, hsl(var(--appistry-signal)) 0%, transparent 70%)" }} />
        <div className="app-container relative py-24 md:py-32">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 mb-8 bg-[hsl(var(--appistry-signal))]">
            <Lightning size={14} weight="fill" />
            <span className="text-[10px] font-mono uppercase tracking-[2.5px] font-bold">APPISTRY IDEALAB™ · AI-ASSISTED DISCOVERY</span>
          </div>
          <h1 className="text-5xl md:text-7xl font-black tracking-tighter leading-[0.95] mb-6 max-w-5xl" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="idealab-hero-headline">
            Turn your idea into a practical digital solution.
          </h1>
          <p className="text-lg md:text-xl text-zinc-300 max-w-3xl leading-relaxed mb-10">
            Most discovery feels like paperwork. IdeaLab is a guided conversation with an AI co-pilot that helps you articulate your idea, asks the right follow-up questions, and gives you a branded discovery pack within seconds — yours to keep, even if you don't go further with us.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={startSession} className="btn-signal" data-testid="idealab-start-cta">
              Start a discovery session <ArrowRight size={18} weight="bold" />
            </button>
            {localToken && (
              <Link to={`/idealab/start?resume=${localToken}`} className="btn-ghost-ink !text-white !border-white/30 hover:!bg-white/10" data-testid="idealab-resume-cta">
                Resume my session
              </Link>
            )}
          </div>
          <p className="mt-6 text-xs font-mono uppercase tracking-[2.5px] text-zinc-500">
            ★ 10–30 MINUTES · NO LOGIN · SAVED AUTOMATICALLY · FREE
          </p>
        </div>
      </section>

      {/* WHY IDEALAB */}
      <section className="border-b border-zinc-200 bg-white">
        <div className="app-container py-20 md:py-24">
          <p className="overline mb-4">Why IdeaLab</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight max-w-3xl mb-14" style={{ fontFamily: "'Outfit', sans-serif" }}>
            A discovery experience that actually helps you think.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-zinc-200">
            {HOOKS.map(({ i: Icon, t, d }, idx) => (
              <div key={t} className="p-8 md:p-10 border-r border-b border-zinc-200" data-testid={`idealab-hook-${idx}`}>
                <Icon size={28} weight="fill" className="text-[hsl(var(--appistry-signal))] mb-4" />
                <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{t}</h3>
                <p className="text-zinc-600 leading-relaxed text-sm">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="border-b border-zinc-200">
        <div className="app-container py-20 md:py-24">
          <p className="overline mb-4">What we'll cover</p>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight max-w-3xl mb-3" style={{ fontFamily: "'Outfit', sans-serif" }}>
            11 short steps from start to finish.
          </h2>
          <p className="text-zinc-600 max-w-2xl mb-12">Roughly <strong>10–30 minutes</strong> end-to-end depending on how much detail you share — and you can skip any question you can&apos;t answer yet.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 md:grid-rows-6 md:grid-flow-col gap-x-12 gap-y-3 max-w-4xl">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-start gap-4 py-3 border-b border-zinc-100">
                <span className="text-xs font-mono text-zinc-400 font-bold w-6">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-zinc-800">{s}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONFIDENTIALITY */}
      <section className="bg-zinc-50 border-b border-zinc-200">
        <div className="app-container py-16 md:py-20">
          <div className="max-w-3xl">
            <ShieldCheck size={28} weight="fill" className="text-[hsl(var(--appistry-signal))] mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Your idea is yours. We treat it like it.
            </h2>
            <p className="text-zinc-600 leading-relaxed">
              We understand your idea may contain commercially sensitive information. Appistry treats every submission as confidential and uses it only to evaluate your enquiry and discuss potential solutions with you. Submission does not create a contractual relationship.
            </p>
            <p className="mt-4">
              <Link to="/privacy" className="text-sm font-semibold underline underline-offset-4">Read our privacy policy →</Link>
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-zinc-950 text-white">
        <div className="app-container py-20 md:py-24 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Ready to think it through?
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto mb-8">
            10–30 minutes, no commitment. Walk away with a discovery pack you can use even if we never speak.
          </p>
          <button onClick={startSession} className="btn-signal inline-flex" data-testid="idealab-final-cta">
            Start now <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </section>
    </div>
  );
}
